/** Deterministic world-space breakup shared by terrain and opaque static surfaces.
 * Exposure is normal/material based; interiors do not yet have roof shelter data.
 * Snow is albedo only and never changes collision, geometry, or actor wetness. */
export const SNOW_SURFACE_GLSL = `
float snowHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float snowNoise(vec2 p) {
  vec2 cell = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(snowHash(cell), snowHash(cell + vec2(1.0, 0.0)), f.x),
             mix(snowHash(cell + vec2(0.0, 1.0)), snowHash(cell + vec2(1.0)), f.x), f.y);
}
float snowSurfaceMask(vec3 world, vec3 normal, float cover) {
  float retention = smoothstep(0.45, 0.92, normal.y);
  float breakup = 0.70 * snowNoise(world.xz * 0.48) + 0.30 * snowNoise(world.xz * 2.3);
  float threshold = 0.02 + breakup * 0.78;
  return smoothstep(threshold, threshold + 0.16, clamp(cover, 0.0, 1.0)) * retention;
}
vec3 snowSurfaceColor(vec3 world, vec3 underlying) {
  float dirt = snowNoise(world.xz * 3.2);
  return mix(vec3(0.71, 0.755, 0.78), vec3(0.82, 0.85, 0.86), dirt) * 0.97 + underlying * 0.03;
}
`;

/** CPU acceptance reference for patch growth and surface orientation. */
export function snowSurfaceMask(x: number, z: number, normalY: number, cover: number): number {
  const patch = 0.7 * noise(x * 0.48, z * 0.48) + 0.3 * noise(x * 2.3, z * 2.3);
  const threshold = 0.02 + patch * 0.78;
  return smooth(threshold, threshold + 0.16, Math.max(0, Math.min(1, cover))) * smooth(0.45, 0.92, normalY);
}
function smooth(a: number, b: number, value: number): number { const t = Math.max(0, Math.min(1, (value - a) / (b - a))); return t * t * (3 - 2 * t); }
function hash(x: number, z: number): number { const n = Math.sin(x * 127.1 + z * 311.7) * 43758.5453; return n - Math.floor(n); }
function noise(x: number, z: number): number {
  const ix = Math.floor(x), iz = Math.floor(z), fx = smooth(0, 1, x - ix), fz = smooth(0, 1, z - iz);
  const a = hash(ix, iz) * (1 - fx) + hash(ix + 1, iz) * fx;
  const b = hash(ix, iz + 1) * (1 - fx) + hash(ix + 1, iz + 1) * fx;
  return a * (1 - fz) + b * fz;
}
